import { useState, useEffect, useRef } from "react";
import { useContext, memo } from "react";
import Transmitter from "../components/Transmitter";
import ControlValveControlPanel from "../components/ControlValveControlPanel";
import Valve from "../components/Valve";
import "./PFRefluxCond1.css";
import { WellContext } from "../context/WellContext";
import { ProcessContext } from "../context/ProcessContext";
import PumpFaceplate from "../components/PumpFaceplate";
import AirCooler from "../components/AirCooler";
import AirCoolerFaceplate from "../components/AirCoolerFaceplate";
import { AirCoolerContext } from "../context/AirCoolerContext";
import { PlantStore } from "../engine/PlantStore";
import { usePlantStore } from "../engine/usePlantStore";
import { trackPageVisit } from "../competency/PageTracker";
import TransmitterFaceplate from "../components/TransmitterFaceplate";





function ValveOutput({
    tag,
    value,
    x,
    y,
    r = 0,
    description = "Control Valve Opening"
}) {

    const [hover, setHover] = useState(false);
    const [showFaceplate, setShowFaceplate] = useState(false);

    return (
        <>
            {/* Hover */}
            {hover && (
                <div
                    style={{
                        position: "absolute",
                        left: x + 30,
                        top: y - 5,
                        background: "rgba(0,80,180,.85)",
                        color: "#fff",
                        padding: "5px 8px",
                        borderRadius: "4px",
                        border: "1px solid #4fc3ff",
                        fontSize: "11px",
                        zIndex: 9999,
                        pointerEvents: "none",
                        whiteSpace: "nowrap"
                    }}
                >
                    <strong>{tag}</strong><br />
                    {description}
                </div>
            )}

            {/* Original Valve Output */}
            <div
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onClick={() => setShowFaceplate(true)}
                style={{
                    position: "absolute",
                    left: x,
                    top: y,

                    color: "lime",
                    background: "black",

                    fontSize: "10px",
                    padding: "1px 4px",

                    borderRadius: "4px",
                    border: "1px solid #555",

                    transform: `rotate(${r}deg)`,
                    transformOrigin: "center",

                    cursor: "pointer",

                    fontWeight: "bold",

                    minWidth: "22px",
                    textAlign: "center",

                    transition: "0.2s",
                    boxShadow: hover
                        ? "0 0 8px rgba(0,255,255,.7)"
                        : "none"
                }}
            >
                {Number(value).toFixed(1)} %
            </div>

            {showFaceplate && (
                <TransmitterFaceplate
                    tag={tag}
                    value={Number(value)}
                    unit="%"
                    min={0}
                    max={100}
                    HH={100}
                    H={90}
                    L={10}
                    LL={0}
                    description={description}
                    x={x + 35}
                    y={y + 20}
                    onClose={() => setShowFaceplate(false)}
                />
            )}
        </>
    );
}




function AirCoolerPage() {
  const [openPanels, setOpenPanels] = useState([]);

  const openPanel = (tag) => {
    setOpenPanels((prev) =>
      prev.includes(tag) ? prev : [...prev, tag]
    );
  };

  const closePanel = (tag) => {
    setOpenPanels((prev) => prev.filter((p) => p !== tag));
  };

  return (
    <div>
      <AirCooler tag="M101A_A" x={945} y={322}/>
      <AirCooler tag="M101A_B" x={988} y={322}/>
      <AirCooler tag="M101B_A" x={945} y={552}/>
      <AirCooler tag="M101B_B" x={988} y={552}/>
      <AirCooler tag="M101C_A" x={1180} y={322}/>
      <AirCooler tag="M101C_B" x={1223} y={322}/>
      <AirCooler tag="M101D_A" x={1180} y={552}/>
      <AirCooler tag="M101D_B" x={1223} y={552}/>
      {openPanels.map((tag, i) => (
        <AirCoolerFaceplate
          key={tag}
          tag={tag}
          close={() => closePanel(tag)}
          defaultPosition={{ x: 300 + i * 40, y: 120 + i * 40 }}
        />
      ))}
    </div>
  );
}


function PFRefluxCond1() {
  // ✅ Use ref to track render count (for debugging only)
  const renderCount = useRef(0);
  renderCount.current += 1;
  console.log(`PFRefluxCond1 Render: ${renderCount.current}`);

  // ✅ Track page visit only once
  useEffect(() => {
    trackPageVisit("PFRefluxCond1");
  }, []);

  const {processData,  setProcessData} = useContext(ProcessContext);
  const { toggleValve, valves } = useContext(WellContext);
  const [hoveredValve, setHoveredValve] = useState(null);

  const DEFAULT_VALVES = {
    FCV_17001: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_17002: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_17003: { mode: "Manual", opening: 0, sp: "", pv: "" },
  };

  const [valveData, setValveData] = useState(() => {
    const saved = localStorage.getItem("PFRefluxCond1_valveData");
    if (!saved) return DEFAULT_VALVES;
    try {
      const parsed = JSON.parse(saved);
      return {
        FCV_17001: { ...DEFAULT_VALVES.FCV_17001, ...parsed.FCV_17001 },
        FCV_17002: { ...DEFAULT_VALVES.FCV_17002, ...parsed.FCV_17002 },
        FCV_17003: { ...DEFAULT_VALVES.FCV_17003, ...parsed.FCV_17003 },
      };
    } catch {
      return DEFAULT_VALVES;
    }
  });

  /* ✅ Persist */
  useEffect(() => {
    localStorage.setItem("PFRefluxCond1_valveData", JSON.stringify(valveData));
  }, [valveData]);

  const updateValveData = (tag, field, value) => {
    setValveData(prev => ({
      ...prev,
      [tag]: {
        ...(prev[tag] || DEFAULT_VALVES[tag]),
        [field]: field === "opening" ? Number(value) || 0 : value
      }
    }));
  };

  /* ================= PANEL CONTROL ================= */
  const [openPanels, setOpenPanels] = useState([]);
  const openPanel = (tag) => {
    if (!openPanels.includes(tag)) {
      setOpenPanels(prev => [...prev, tag]);
    }
  };
  const closePanel = (tag) => {
    setOpenPanels(prev => prev.filter(p => p !== tag));
  };

  // ================= PUMP PANEL LOGIC =================
  const [openPumpPanels, setOpenPumpPanels] = useState([]);
  const openPumpPanel = (tag) => {
    if (!openPumpPanels.includes(tag)) {
      setOpenPumpPanels(prev => [...prev, tag]);
    }
  };
  const closePumpPanel = (tag) => {
    setOpenPumpPanels(prev => prev.filter(p => p !== tag));
  };

  // ================= AIR COOLER PANEL =================
  const [openCoolerPanels, setOpenCoolerPanels] = useState([]);
  const openCoolerPanel = (tag) => {
    setOpenCoolerPanels((prev) =>
      prev.includes(tag) ? prev : [...prev, tag]
    );
  };
  const closeCoolerPanel = (tag) => {
    setOpenCoolerPanels((prev) => prev.filter((p) => p !== tag));
  };

  const tagStyle = {
    position: "absolute",
    top: -30,
    left: "50%",
    transform: "translateX(-50%)",
    background: "black",
    color: "#00ffcc",
    fontSize: "13px",
    fontWeight: "bold",
    padding: "3px 8px",
    borderRadius: "4px",
    border: "1px solid #555",
    whiteSpace: "nowrap",
    zIndex: 999
  };

  const renderInput = ({
    name,
    value,
    setter,
    x,
    y,
    w,
    h,
    bg = "#222",
    color = "#222"
  }) => (
    <input
      value={value}
      onChange={(e) => setter(e.target.value)}
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        height: h,
        backgroundColor: bg,
        color,
        border: "1px solid #777",
        borderRadius: "4px",
        padding: "1px 2px",
        fontSize: "14px",
        zIndex: 20,
        textAlign: "center"
      }}
    />
  );

  const A101A_Loovers =  processData.A101A_Loovers ?? 100;
  const A101B_Loovers =  processData.A101B_Loovers ?? 100;
  const A101C_Loovers =  processData.A101C_Loovers ?? 100;
  const A101D_Loovers =  processData.A101D_Loovers ?? 80;
  const ATM_Air =  processData.ATM_Air ?? 25;

  const setA101A_Loovers = (value) => {
  setProcessData(prev => ({
    ...prev,
    A101A_Loovers: Number(value)
  }));
};

const setA101B_Loovers = (value) => {
  setProcessData(prev => ({
    ...prev,
    A101B_Loovers: Number(value)
  }));
};

const setA101C_Loovers = (value) => {
  setProcessData(prev => ({
    ...prev,
    A101C_Loovers: Number(value)
  }));
};

const setA101D_Loovers = (value) => {
  setProcessData(prev => ({
    ...prev,
    A101D_Loovers: Number(value)
  }));
};

const setATM_Air = (value) => {
  setProcessData(prev => ({
    ...prev,
    ATM_Air: Number(value)
  }));
};


  // ================= RETREIVING OF DATA FROM OTHER PAGES VIA LOCAL STROAGE =================
  const { transmitters } = usePlantStore();
  const FIC_16009 = transmitters.FIC_16009 || 0;
  const PT_16002 = transmitters.PT_16002 || 0;
  const TT_16002 = transmitters.TT_16002 || 0;


/* ==========================================
   BACK PRESSURE FROM REFLUX DRUM
========================================== */

const BackPressureFactor =
Number(
  localStorage.getItem(
    "PF_BackPressure"
  )
) || 0;

localStorage.setItem(
  "BackPressureFactor",
  BackPressureFactor
);



  const xv17001Open = !valves?.XV_17001;
  const FIC_17001 = FIC_16009;
  const TT_17001 = TT_16002;
  const PT_17001 = PT_16002 + (BackPressureFactor * 0.40);
  const PT_17002 = (PT_17001 * 0.3) + (BackPressureFactor * 0.60);
  const TT_17004 = xv17001Open ? 27.564 : 22;



  

  // ======================================================
  // PRE FLASH OVERHEAD VAPOR AIR COOLER CONDENSING LOGIC
  // ======================================================
  const { coolerData } = useContext(AirCoolerContext);

  // OUTPUT TEMPERATURE
  const [TT_17002, setTT_17002] = useState(0);

  // ======================================================
  // GET MOTOR DATA
  // ======================================================
  const getMotor = (tag) => {
    return coolerData?.[tag] || { speed: 0, running: false };
  };

  const M101A_A = getMotor("M101A_A");
  const M101A_B = getMotor("M101A_B");
  const M101B_A = getMotor("M101B_A");
  const M101B_B = getMotor("M101B_B");
  const M101C_A = getMotor("M101C_A");
  const M101C_B = getMotor("M101C_B");
  const M101D_A = getMotor("M101D_A");
  const M101D_B = getMotor("M101D_B");

  // ======================================================
  // COOLER SPEED CALCULATION
  // ======================================================
  const getCoolerSpeed = (A, B) => {
    let speed = 0;
    if (A.running && B.running) {
      speed = (A.speed + B.speed) / 2;
    } else if (A.running) {
      speed = A.speed;
    } else if (B.running) {
      speed = B.speed;
    }
    return speed;
  };

  const A101Speed = getCoolerSpeed(M101A_A, M101A_B);
  const A102Speed = getCoolerSpeed(M101B_A, M101B_B);
  const A103Speed = getCoolerSpeed(M101C_A, M101C_B);
  const A104Speed = getCoolerSpeed(M101D_A, M101D_B);

  // ======================================================
  // RUNNING STATUS
  // ======================================================
  const A101_ON = M101A_A.running || M101A_B.running;
  const A102_ON = M101B_A.running || M101B_B.running;
  const A103_ON = M101C_A.running || M101C_B.running;
  const A104_ON = M101D_A.running || M101D_B.running;

  // ======================================================
  // LOUVERS
  // ======================================================
  const safeLouver = (value) => Math.max(0, Math.min(100, Number(value) || 0));

  const L101 = safeLouver(A101A_Loovers);
  const L102 = safeLouver(A101B_Loovers);
  const L103 = safeLouver(A101C_Loovers);
  const L104 = safeLouver(A101D_Loovers);

  // ======================================================
  // ATMOSPHERIC AIR EFFECT
  // ======================================================
  const atmFactor = Math.max(0.35, 1 - ((Number(ATM_Air) - 25) / 50));

  // ======================================================
  // COOLING DUTY
  // ======================================================
  const duty = (running, speed, louver) => {
    if (!running) return 0;
    const motorCapacity = 28;
    const louverFactor = louver / 100;
    return motorCapacity * (speed / 100) * louverFactor * atmFactor;
  };

  const totalCooling =
    duty(A101_ON, A101Speed, L101) +
    duty(A102_ON, A102Speed, L102) +
    duty(A103_ON, A103Speed, L103) +
    duty(A104_ON, A104Speed, L104);

  // ======================================================
  // TT-17002
  // ======================================================
  const newTT17002 = Number(TT_17001) - totalCooling;

  // ✅ FIXED: Use refs to track values and prevent re-renders
  const TT_17002_Ref = useRef(TT_17002);
  const TT_17003_Ref = useRef(0);
  const TT_17005_Ref = useRef(0);
  const intervalRef = useRef(null);
  const isMountedRef = useRef(false);

  // ✅ Update TT_17002 when dependencies change
  useEffect(() => {
    const calculated = Math.max(newTT17002, Number(ATM_Air));
    TT_17002_Ref.current = calculated;
    setTT_17002(calculated);
  }, [TT_17001, ATM_Air, A101Speed, A102Speed, A103Speed, A104Speed, L101, L102, L103, L104]);

  
  
/* =========================================================
   E-113 TAGS
========================================================= */

const TT_17003 = processData.TT_17003;
const TT_17005 = processData.TT_17005;


/* =========================================================
   E-113 REFS
========================================================= */

const E113_intervalRef = useRef(null);
const E113_isMounted = useRef(false);

const E113_processRef = useRef({});


/* =========================================================
   KEEP LIVE VALUES UPDATED
========================================================= */

useEffect(() => {

  E113_processRef.current = {

    TT_17002,
    TT_17004

  };

});


/* =========================================================
   E-113 HEAT EXCHANGER
========================================================= */

useEffect(() => {

  if (E113_isMounted.current) return;

  E113_isMounted.current = true;

  console.log("✅ E-113 interval created");

  E113_intervalRef.current = setInterval(() => {

    const {

      TT_17002,
      TT_17004

    } = E113_processRef.current;


    /* =====================================
       INLETS
    ===================================== */

    const stream1 =
      Number(TT_17002) || 0;

    const stream2 =
      Number(TT_17004) || 0;


    let targetTT17003 = stream1;
    let targetTT17005 = stream2;


    /* =====================================
       DETERMINE HOT/COLD STREAM
    ===================================== */

    const hotter =
      Math.max(stream1, stream2);

    const colder =
      Math.min(stream1, stream2);

    const deltaT =
      hotter - colder;


    /* =====================================
       HEAT EXCHANGE
    ===================================== */

    if (deltaT > 0.05) {

      const effectiveness = 0.22;

      const duty = Math.max(
        0.20,
        deltaT * effectiveness
      );

      const secondaryDuty =
        duty * 0.70;

      if (stream1 >= stream2) {

        // Stream 1 hotter

        targetTT17003 =
          stream1 - duty;

        targetTT17005 =
          stream2 + secondaryDuty;

      }

      else {

        // Stream 2 hotter

        targetTT17003 =
          stream1 + secondaryDuty;

        targetTT17005 =
          stream2 - duty;

      }

    }


    /* =====================================
       PHYSICAL LIMITS
    ===================================== */

    if (stream1 >= stream2) {

      targetTT17003 =
        Math.max(
          targetTT17003,
          stream2 + 0.5
        );

      targetTT17005 =
        Math.min(
          targetTT17005,
          stream1 - 0.5
        );

    }

    else {

      targetTT17005 =
        Math.max(
          targetTT17005,
          stream1 + 0.5
        );

      targetTT17003 =
        Math.min(
          targetTT17003,
          stream2 - 0.5
        );

    }


    /* =====================================
       THERMAL LAG
    ===================================== */

    setProcessData(prev => ({

      ...prev,

      TT_17003: Number(

        (

          (prev.TT_17003 ?? stream1)

          +

          (

            targetTT17003 -

            (prev.TT_17003 ?? stream1)

          ) * 0.08

        ).toFixed(2)

      ),

      TT_17005: Number(

        (

          (prev.TT_17005 ?? stream2)

          +

          (

            targetTT17005 -

            (prev.TT_17005 ?? stream2)

          ) * 0.08

        ).toFixed(2)

      )

    }));

  }, 1000);


  return () => {

    console.log("❌ E-113 interval cleared");

    if (E113_intervalRef.current) {

      clearInterval(E113_intervalRef.current);

      E113_intervalRef.current = null;

    }

    E113_isMounted.current = false;

  };

}, []);





  // ✅ FIXED: Update PlantStore without causing re-renders
  useEffect(() => {
    const current = PlantStore.get().transmitters;
    
    if (
      current.TT_17003 === TT_17003 &&
      current.PT_17002 === PT_17002 &&
      current.FIC_17001 === FIC_17001
    ) {
      return;
    }

    PlantStore.set({
      transmitters: {
        ...current,
        TT_17003,
        PT_17002,
        FIC_17001,
      }
    });
  }, [TT_17003, PT_17002, FIC_17001]);

  return (
    <div className="PFRefluxCond1-container">
      {/* TITLE */}
      <h2 style={{
        position: "absolute",
        top: "0px",
        left: "240px",
        color: "white",
        background: "rgb(30, 5, 250)",
        padding: "2px 243px",
        zIndex: 10,
      }}>
        PRE-FLASH TOWER CONDENSER
      </h2>

      {/* =================SHUTDOWN VALVES ================= */}
      <Valve
        className="XV-17001"
        tag="XV_17001"
        x={287}
        y={426}
        isClosed={valves.XV_17001}
        toggle={toggleValve}
      />

      {renderInput({
        name: "A-101A Loovers",
        value: A101A_Loovers,
        setter: setA101A_Loovers,
        x: 490,
        y: 170,
        bg: "#d7f1d7",
        w: 50,
        h: 20,
      })}

      {renderInput({
        name: "A-101B Loovers",
        value: A101B_Loovers,
        setter: setA101B_Loovers,
        x: 750,
        y: 170,
        bg: "#bde7bd",
        w: 50,
        h: 20,
      })}

      {renderInput({
        name: "A-101C Loovers",
        value: A101C_Loovers,
        setter: setA101C_Loovers,
        x: 1010,
        y: 170,
        bg: "#abd6ab",
        w: 50,
        h: 20,
      })}

      {renderInput({
        name: "A-101D Loovers",
        value: A101D_Loovers,
        setter: setA101D_Loovers,
        x: 1250,
        y: 170,
        bg: "#aad6aa",
        w: 50,
        h: 20,
      })}

      {renderInput({
        name: "ATM Air",
        value: ATM_Air,
        setter: setATM_Air,
        x: 1250,
        y: 70,
        bg: "#6584a1",
        w: 50,
        h: 20,
      })}

      {/* ================= CONTROL VALVE FACEPLATES ================= */}
      {openPanels.map((tag, index) => (
        <ControlValveControlPanel
          key={tag}
          tag={tag}
          data={valveData?.[tag] ?? DEFAULT_VALVES?.[tag]}
          update={updateValveData}
          close={() => closePanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= PUMPS CONTROL VALVE FACEPLATES ================= */}
      {openPumpPanels.map((tag, index) => (
        <PumpFaceplate
          key={tag}
          tag={tag}
          close={() => closePumpPanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= ✅ AIR COOLER FACEPLATES ================= */}
      {openCoolerPanels.map((tag, index) => (
        <AirCoolerFaceplate
          key={tag}
          tag={tag}
          close={() => closeCoolerPanel(tag)}
          defaultPosition={{
            x: 400 + index * 40,
            y: 200 + index * 40
          }}
        />
      ))}

      <Transmitter
        tag="TT_17001"
        value={[TT_17001]}
        x={212}
        y={86}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF OVERHEAD VAPOR AT THE TOP OF PREFLASH FRACTIONATION TOWER (T-101) TO A-101 A-D"
      />

      <Transmitter
        tag="TT_17002"
        value={[TT_17002]}
        x={633}
        y={396}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CONDENSED NAPHTHA & NON CONDENSABLE GASES FROM A-101 A-D TO V-101"
      />

      <Transmitter
        tag="PT_17001"
        value={[PT_17001]}
        x={352}
        y={86}
        unit="MPa"
        max={1}
        HH={0.8}
        H={0.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF OVERHEAD VAPOR AT THE TOP OF PREFLASH FRACTIONATION TOWER (T-101) TO A-101 A-D"
      />

      <Transmitter
        tag="FIC_17001"
        value={[FIC_17001]}
        x={492}
        y={86}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF OVERHEAD VAPOR OF PREFLASH TOWER TO A-101A-D TO A-101 A-D"
      />

      <Transmitter
        tag="TT_17004"
        value={[TT_17004]}
        x={162}
        y={406}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FRESH WATER SUPPLY TO E-113 (PREFLASH OVHD VAPOR CONDENSER)"
      />

      <Transmitter
        tag="TT_17005"
        value={[TT_17005]}
        x={162}
        y={622}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FRESH WATER RETURN OUT OF E-113 TO WATER RETURN MANIFOLD"
      />

      <Transmitter
        tag="TT_17003"
        value={[TT_17003]}
        x={962}
        y={629}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CONDENSED NAPHTHA & NON CONDENSABLE GASES FROM A-101 A-D AND PREFLASH NAPHTHA AFTER COOLER TO V-101"
      />

      <Transmitter
        tag="PT_17002"
        value={[PT_17002]}
        x={792}
        y={629}
        unit="MPa"
        max={1}
        HH={0.8}
        H={0.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF OVERHEAD VAPOR AT THE TOP OF PREFLASH FRACTIONATION TOWER (T-101) TO A-101 A-D"
      />

      <AirCooler
        tag="M101A_A"
        x={366}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M101A_A")}
      />

      <AirCooler
        tag="M101A_B"
        x={411}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M101A_B")}
      />

      <AirCooler
        tag="M101B_A"
        x={639}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M101B_A")}
      />

      <AirCooler
        tag="M101B_B"
        x={684}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M101B_B")}
      />

      <AirCooler
        tag="M101C_A"
        x={895}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M101C_A")}
      />

      <AirCooler
        tag="M101C_B"
        x={942}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M101C_B")}
      />

      <AirCooler
        tag="M101D_A"
        x={1144}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M101D_A")}
      />

      <AirCooler
        tag="M101D_B"
        x={1189}
        y={311}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M101D_B")}
      />
    </div>
  );
}

export default memo(PFRefluxCond1);